import React from "react";
import { Button, Collapse, Form, Input, InputNumber, Select, Spin, Switch, Table, Tag, Tooltip } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useLazyFetchLowStockReportQuery } from "features/store";

const { Panel } = Collapse;

const MATERIAL_TYPES = ["CLOTH MATERIAL", "CAP MATERIAL", "OTHER MATERIAL"];

const columns = [
  { title: "Material Type", dataIndex: "material_type", key: "material_type" },
  { title: "Material Name", dataIndex: "material_name", key: "material_name" },
  { title: "Color", dataIndex: "color", key: "color" },
  { title: "Sub Type", dataIndex: "sub_type", key: "sub_type" },
  { title: "Current Qty", dataIndex: "current_quantity", key: "current_quantity" },
  { title: "Unit", dataIndex: "unit", key: "unit" },
  { title: "Last Updated", dataIndex: "last_updated", key: "last_updated" },
  { title: "Updated By", dataIndex: "updated_by", key: "updated_by" },
];

const csvHeaders = [
  { label: "Material Type", key: "material_type" },
  { label: "Material Name", key: "material_name" },
  { label: "Color", key: "color" },
  { label: "Sub Type", key: "sub_type" },
  { label: "Current Qty", key: "current_quantity" },
  { label: "Unit", key: "unit" },
  { label: "Last Updated", key: "last_updated" },
  { label: "Updated By", key: "updated_by" },
];

const rowClassName = (record) => {
  if (record.current_quantity === 0) return "low-stock-zero";
  if (record.current_quantity > 0) return "low-stock-amber";
  return "";
};

const InvRepLowStock = () => {
  const [form] = Form.useForm();
  const [trigger, { data, isLoading, isFetching }] = useLazyFetchLowStockReportQuery();

  const handleApply = () => {
    const v = form.getFieldsValue();
    const params = {};
    if (v.threshold != null) params.threshold = v.threshold;
    params.include_zero = v.include_zero !== false ? "true" : "false";
    if (v.type) params.type = v.type;
    if (v.color) params.color = v.color;
    if (v.unit) params.unit = v.unit;
    trigger(params);
  };

  const handleClear = () => {
    form.resetFields();
    form.setFieldsValue({ threshold: 10, include_zero: true });
    trigger({ threshold: 10, include_zero: "true" });
  };

  const results = data?.results || [];
  const count = data?.count ?? results.length;
  const threshold = form.getFieldValue("threshold") ?? 10;

  return (
    <div style={{ padding: "24px" }}>
      <style>{`
        .low-stock-zero { background-color: #fff1f0 !important; }
        .low-stock-amber { background-color: #fffbe6 !important; }
      `}</style>
      <h2 style={{ marginBottom: 16 }}>Low Stock Alert Report</h2>

      <Collapse defaultActiveKey={["filters"]} style={{ marginBottom: 16 }}>
        <Panel header="Filters" key="filters">
          <Form
            form={form}
            layout="inline"
            initialValues={{ threshold: 10, include_zero: true }}
            style={{ flexWrap: "wrap", gap: "8px 0" }}
          >
            <Form.Item name="threshold" label="Threshold">
              <InputNumber min={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="include_zero" label="Include Zero Stock" valuePropName="checked">
              <Switch defaultChecked />
            </Form.Item>
            <Form.Item name="type" label="Material Type">
              <Select allowClear placeholder="All types" style={{ width: 180 }}>
                {MATERIAL_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="color" label="Color">
              <Input placeholder="Color" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="unit" label="Unit">
              <Input placeholder="Unit" style={{ width: 100 }} />
            </Form.Item>
          </Form>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleApply} loading={isLoading || isFetching}>
              Apply Filters
            </Button>
            <Button onClick={handleClear}>Clear Filters</Button>
          </div>
        </Panel>
      </Collapse>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        {data && (
          <>
            <Tag color="orange" style={{ fontSize: 14 }}>Threshold: {threshold}</Tag>
            <Tag color="red" style={{ fontSize: 14 }}>Alerts: {count}</Tag>
          </>
        )}
        {results.length > 0 && (
          <Tooltip title="Export to Excel">
            <CSVLink
              data={results}
              headers={csvHeaders}
              filename="low-stock-alert-report.csv"
            >
              <Button icon={<FileExcelOutlined />} style={{ color: "#217346", borderColor: "#217346" }}>
                Export to Excel
              </Button>
            </CSVLink>
          </Tooltip>
        )}
      </div>

      <Spin spinning={isLoading || isFetching}>
        <Table
          columns={columns}
          dataSource={results}
          rowKey={(r, i) => r.id ?? i}
          rowClassName={rowClassName}
          locale={{ emptyText: "No records found" }}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Spin>
    </div>
  );
};

export default InvRepLowStock;
